import { nanoid } from 'nanoid'
import { RawTrpcClient } from '~/core/trpc'
import { Toolbox } from '~/plugins/toolbox/shared'

let currentPopup: Window | null = null

export const Oauth2Popup = {
  open: openOAuth2Popup,
  openWithLoginUrl,
  openUrlUntilClosed,
  close,
}

function close() {
  currentPopup?.close()
}

async function openWithLoginUrl(loginUrl: string, redirectUrl: string) {
  currentPopup = openWindow(loginUrl)
  return {
    code: await getCode(redirectUrl),
    codeChallenge: undefined,
  }
}

async function openUrlUntilClosed(loginUrl: string) {
  currentPopup = openWindow(loginUrl)

  await onPopupClose(currentPopup)

  return
}

async function openOAuth2Popup(
  params: OAuth2PopupParams,
): Promise<OAuth2PopupResponse> {
  closeOAuth2Popup()

  const pckeChallenge = nanoid()
  const oauthSessionId = Toolbox.Utility.getUUID()

  const url = constructUrl(params, pckeChallenge, oauthSessionId)

  currentPopup = openWindow(url)

  return {
    code: await getCode(params.redirectUrl, oauthSessionId),
    codeChallenge: params.pkce ? pckeChallenge : undefined,
  }
}

function openWindow(url: string): Window | null {
  const winFeatures = [
    'resizable=no',
    'toolbar=no',
    'left=100',
    'top=100',
    'scrollbars=no',
    'menubar=no',
    'status=no',
    'directories=no',
    'location=no',
    'width=600',
    'height=800',
  ].join(', ')
  return window.open(url, '_blank', winFeatures)
}

function closeOAuth2Popup() {
  currentPopup?.close()
}

function constructUrl(
  params: OAuth2PopupParams,
  pckeChallenge: string,
  oauthSessionId: string,
) {
  const queryParams: Record<string, string> = {
    response_type: 'code',
    client_id: params.clientId,
    redirect_uri: params.redirectUrl,
    access_type: 'offline',
    state: oauthSessionId,
    prompt: 'consent',
    scope: params.scope,
    ...(params.extraParams || {}),
  }

  if (params.pkce) {
    queryParams['code_challenge_method'] = 'plain'
    queryParams['code_challenge'] = pckeChallenge
  }

  for (const key in queryParams) {
    if (queryParams[key] === undefined) {
      delete queryParams[key]
    }
  }

  const url = new URL(params.authUrl)

  Object.entries(queryParams).forEach(([key, value]) => {
    url.searchParams.append(key, value)
  })

  return url.toString()
}

function getCode(
  redirectUrl: string,
  oauthSessionId?: string,
): Promise<string> {
  return new Promise<string>(resolve => {
    let interval = null

    const handler = (event: MessageEvent) => {
      if (
        redirectUrl &&
        redirectUrl.startsWith(event.origin) &&
        event.data['code']
      ) {
        resolve(decodeURIComponent(event.data.code))
        currentPopup?.close()
        window.removeEventListener('message', handler)
        clearInterval(interval)
      } else {
        const errorMessage =
          '[OAuth2 Popup] Received message from unexpected origin or without code'
        console.warn(errorMessage, event)
      }
    }

    window.addEventListener('message', handler)

    interval = setInterval(async () => {
      if (!oauthSessionId) {
        return
      }

      const code =
        await RawTrpcClient.dataConnector.getTemporaryOauthCode.query({
          oauthSessionId,
        })

      if (!code) {
        return
      }

      resolve(code)
      currentPopup?.close()
      clearInterval(this)
      window.removeEventListener('message', handler)
    }, 2000)
  })
}

function onPopupClose(popup: Window): Promise<void> {
  return new Promise<void>(resolve => {
    const interval = setInterval(() => {
      if (popup?.closed) {
        clearInterval(interval)
        resolve()
      }
    }, 200)
  })
}

type OAuth2PopupParams = {
  authUrl: string
  clientId: string
  redirectUrl: string
  scope: string
  pkce: boolean
  extraParams?: Record<string, string>
}

type OAuth2PopupResponse = {
  code: string
  codeChallenge: string | undefined
}
